class GooglePlacesService {
  constructor() {
    this.apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
    this.baseUrl = 'https://d128sk95sboo22.cloudfront.net/maps/api/place'
  }

  // Method to get autocomplete predictions
  async getAutocompletePredictions(input, options = {}) {
    const params = new URLSearchParams({
      input: input,
      key: this.apiKey,
    })

    if (options.types) {
      // Convert array to pipe-separated string
      params.append('types', Array.isArray(options.types) ? options.types.join('|') : options.types)
    }

    if (options.language) {
      params.append('language', options.language)
    }

    try {
      const response = await fetch(`${this.baseUrl}/autocomplete/json?${params}`)
      const data = await response.json()

      if (data.status === 'OK') {
        // Convert to match SDK format
        return data.predictions.map(prediction => ({
          description: prediction.description,
          place_id: prediction.place_id,
          structured_formatting: prediction.structured_formatting,
          types: prediction.types,
          terms: prediction.terms
        }))
      } else if (data.status === 'ZERO_RESULTS') {
        return []
      } else {
        throw new Error(`Autocomplete API error: ${data.status}`)
      }
    } catch (error) {
      console.error('Failed to fetch autocomplete predictions:', error)
      throw error
    }
  }

  // Method to get place details by place ID
  async getPlaceDetails(placeId, fields) {
    const defaultFields = [
      'url',
      'name',
      'photos',
      'website',
      'place_id',
      'vicinity',
      'geometry',
      'opening_hours',
      'address_components',
      'formatted_address',
      'formatted_phone_number',
    ]

    const requestFields = fields || defaultFields

    const params = new URLSearchParams({
      place_id: placeId,
      fields: requestFields.join(','),
      key: this.apiKey,
    })

    try {
      const response = await fetch(`${this.baseUrl}/details/json?${params}`)
      const data = await response.json()

      if (data.status === 'OK') {
        const place = data.result

        // Convert photo objects to plain objects with photo references
        if (place.photos && place.photos.length > 0) {
          place.photos = place.photos.map(photo => ({
            photo_reference: photo.photo_reference,
            height: photo.height,
            width: photo.width,
            html_attributions: photo.html_attributions,
            getUrl: (options = {}) => {
              const maxWidth = options.maxWidth || 400
              const maxHeight = options.maxHeight || 400
              
              return this.getPhotoUrl(photo.photo_reference, maxWidth, maxHeight)
            }
          }))
        }

        place.mainImageUrl = place.photos?.[0] ? this.getPhotoUrl(place.photos[0].photo_reference) : null
        place.attribution = place.photos?.[0]?.html_attributions?.[0]

        // Add url field if not present (SDK compatibility)
        if (!place.url && place.place_id) {
          place.url = `https://maps.google.com/?q=place_id:${place.place_id}`
        }

        return place
      } else {
        throw new Error(`Failed to get details for placeId: ${placeId} with status: ${data.status}`)
      }
    } catch (error) {
      console.error('Failed to fetch place details:', error)
      throw error
    }
  }

  // Method to generate photo URL from photo reference
  getPhotoUrl(photoReference, maxWidth = 400, maxHeight = 400) {
    return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${this.apiKey}`
  }

  // Method to get timezone from coordinates using Google Time Zone API
  async getTimeZone(lat, lng) {
    // Google Time Zone API requires a timestamp (use current time)
    const timestamp = Math.floor(Date.now() / 1000)

    const params = new URLSearchParams({
      location: `${lat},${lng}`,
      timestamp: timestamp,
      key: this.apiKey,
    })

    try {
      const response = await fetch(`https://d128sk95sboo22.cloudfront.net/maps/api/timezone/json?${params}`)
      const data = await response.json()

      if (data.status === 'OK') {
        return {
          timeZoneId: data.timeZoneId, // e.g., "America/Los_Angeles"
          timeZoneName: data.timeZoneName, // e.g., "Pacific Daylight Time"
          rawOffset: data.rawOffset,
          dstOffset: data.dstOffset,
        }
      } else {
        console.error(`Time Zone API error: ${data.status}`)
        
        return null
      }
    } catch (error) {
      console.error('Failed to fetch timezone:', error)
      
      return null
    }
  }

  // Legacy method for backward compatibility (deprecated)
  searchNearby(location, radius, keyword) {
    console.warn('searchNearby is deprecated and requires SDK. Consider using direct API calls.')
    throw new Error('searchNearby requires Google Maps SDK')
  }

  // Legacy method for backward compatibility (deprecated)
  textSearch(query, location, radius) {
    console.warn('textSearch is deprecated and requires SDK. Consider using direct API calls.')
    throw new Error('textSearch requires Google Maps SDK')
  }
}

export default GooglePlacesService